import { ArrowLeft, DocumentPdf, Download } from "@carbon/icons-react";
import { Button, InlineNotification } from "@carbon/react";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import Loading from "src/components/DataStates/Loading";
import InlineError from "src/components/Errors/InlineError";
import { DynamicViewRenderer } from "src/dynamic-view-renderer/DynamicViewRenderer";
import { useDynamicViewSchema } from "src/dynamic-view-renderer/DynamicViewSchemaProvider/DynamicViewSchemaProvider";
import { DashboardDefinition } from "src/dynamic-view-renderer/DynamicViewSchemaProvider/DynamicViewSchemaProvider.types";
import {
  DEFAULT_MAX_RELATED_CASES,
  getPrintableConfigurationErrors,
} from "src/dynamic-view-renderer/Views/PrintableView/printableView.utils";
import { usePrintableConfigErrorMessage } from "src/dynamic-view-renderer/Views/PrintableView/usePrintableView";
import { useGetAlertById } from "src/graphql/anti-fraud/queries/alerts/GetAlertByIdQuery";
import useGetRelatedCases from "src/graphql/anti-fraud/queries/alerts/GetRelatedCasesQuery";
import { useClientSchemaQuery } from "src/graphql/anti-fraud/queries/cases/GetCaseSchemasQuery";
import { useDisplayedAlertCase } from "src/hooks/useGetAlertCase";
import { useFormatDate, useT } from "src/internationalization";
import BrandLogo from "src/pages/Sidebar/BrandLogo.tsx";
import { Alert } from "src/types/alert";
import { ClientSchema } from "src/types/clientSchema";

import "src/pages/AlertDetails/alert-pdf/AlertPdfPreviewPage.scss";
import "src/pages/AlertDetails/alert-pdf/AlertPdfPreviewPageContent.scss";

export function AlertPdfPreviewPage() {
  const t = useT();
  const { id } = useParams<{ id: string }>();
  const { data: alert, error, loading } = useGetAlertById({ id });
  const { data: caseSchema, error: schemaError, loading: schemaLoading } = useClientSchemaQuery();
  if (loading || schemaLoading) return <Loading />;
  if (error) return <InlineError {...error} />;
  if (schemaError) return <InlineError {...schemaError} />;
  if (!alert || !caseSchema) return <InlineError message={t("page.alert.pdf.error.message")} />;
  return <LoadedAlertPdfPreviewPage key={alert.id} alert={alert} caseSchema={caseSchema} />;
}

function LoadedAlertPdfPreviewPage({
  alert,
  caseSchema,
}: {
  alert: Alert;
  caseSchema: ClientSchema;
}) {
  const schema = useDynamicViewSchema();
  const location = useLocation();
  const navigate = useNavigate();
  const t = useT();
  const getErrrorMessage = usePrintableConfigErrorMessage();

  const { expectedCase, displayedCase: selectedCase, version } = useDisplayedAlertCase(alert);
  const caseTypeName =
    schema.caseTypeDefinitions.find((type) => type.code === selectedCase?.typeCode)?.label ||
    selectedCase?.configurableType.name ||
    selectedCase?.typeCode ||
    "";
  const versionLabel = {
    creation: t("page.alert.pdf.snapshotFallBack.creation.label"),
    validation: t("page.alert.pdf.snapshotFallBack.validation.label"),
    current: null,
  }[version];

  const returnTo = location.state?.returnTo;

  const backToAlert =
    typeof returnTo === "string" &&
    returnTo.startsWith(`/alert/${alert.id}/`) &&
    !returnTo.startsWith(`/alert/${alert.id}/preview`)
      ? returnTo
      : `/alert/${alert.id}/SynthesisDashboard`;

  const alertPdf = schema.alertPdfDefinition;
  const caseTypesConf = alertPdf?.caseTypes;
  const caseTypeCode = selectedCase?.typeCode.toLowerCase();
  const caseTypeCodeConf = caseTypesConf
    ? Object.keys(caseTypesConf).find((k) => k.toLowerCase() === caseTypeCode)
    : undefined;
  const hasPdfConfiguration = caseTypesConf && caseTypeCodeConf && caseTypesConf[caseTypeCodeConf];

  const widgets = hasPdfConfiguration ? caseTypesConf[caseTypeCodeConf].widgets : [];
  const title = hasPdfConfiguration
    ? caseTypesConf[caseTypeCodeConf].title
    : t("page.alert.pdf.report.title");

  const alertPdfDashboard = "AlertPdfDashboard";

  const dashboard: DashboardDefinition = {
    code: alertPdfDashboard,
    name: t("page.alert.pdf.report.dashboard.title"),
    configurableTypeCode: alertPdfDashboard,
    rows: [{ widgets }],
  };

  const viewConfiguration = {
    type: "printableView",
    dashboards: hasPdfConfiguration ? ["AlertPdfDashboard"] : [],
  };
  const configurationErrors = getPrintableConfigurationErrors(
    viewConfiguration,
    [dashboard],
    schema.widgetDefinitions
  );

  const relatedTables = hasPdfConfiguration
    ? caseTypesConf[caseTypeCodeConf].widgets.filter((w) => w.widgetCode === "relatedCases")
    : [];

  const needsRelatedCases =
    relatedTables.length > 0 && !!selectedCase && configurationErrors.length === 0;

  const relatedLimit = relatedTables.length
    ? Math.max(
        ...relatedTables.map((widget) => widget.maxRelatedCases ?? DEFAULT_MAX_RELATED_CASES)
      )
    : DEFAULT_MAX_RELATED_CASES;

  const related = useGetRelatedCases(
    {
      caseId: selectedCase?.id ?? "",
      options: {
        identifierValues: [],
        pagination: { offset: 0, limit: relatedLimit || DEFAULT_MAX_RELATED_CASES },
      },
    },
    { skip: !needsRelatedCases }
  );

  const relatedReady =
    !needsRelatedCases || (!related.loading && !related.hasError && !!related.data);

  const [loadedAt] = useState(() => new Date());
  const [printing, setPrinting] = useState(false);
  const [printError, setPrintError] = useState(false);
  const [fontsReady, setFontsReady] = useState(false);
  const [logoStatus, setLogoStatus] = useState<"loading" | "ready" | "error">("loading");
  const ready =
    !!selectedCase &&
    configurationErrors.length === 0 &&
    fontsReady &&
    logoStatus === "ready" &&
    relatedReady;
  const formatDate = useFormatDate();

  const date = (value: Date | null) =>
    value
      ? formatDate(value, { dateStyle: "medium", timeStyle: "short" })
      : t("page.alert.pdf.date.unavailable");
  const fallbackNote =
    expectedCase === null && selectedCase
      ? t(
          schema.defaultAlertVersion === "validation"
            ? "page.alert.pdf.snapshotFallback.validation"
            : "page.alert.pdf.snapshotFallback.creation",
          { caseType: caseTypeName, date: date(loadedAt) }
        )
      : null;

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${t("page.alert.pdf.preview.title")}· ${t("page.alert.title", {
      number: alert.alertNumber,
    })}`;
    document.body.classList.add("alert-pdf-active", "alert-pdf-preview-active");
    return () => {
      document.title = previousTitle;
      document.body.classList.remove("alert-pdf-active", "alert-pdf-preview-active");
    };
  }, [alert.alertNumber, t]);

  useEffect(() => {
    let active = true;
    void document.fonts.ready.then(() => {
      if (active) setFontsReady(true);
    });
    return () => {
      active = false;
    };
  }, []);

  const print = async () => {
    if (!ready) return;
    setPrinting(true);
    setPrintError(false);
    try {
      await document.fonts.ready;
      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
      );
      window.print();
    } catch {
      setPrintError(true);
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div className="alert-pdf max-w-[1200px] mx-auto text-textPrimary">
      <header className="pdf-print-hidden sticky top-9 z-10 flex items-center justify-between flex-wrap gap-3 md:gap-x-6 md:gap-y-4 py-5 px-4 md:px-8 bg-field">
        <div className="flex-1 min-w-[220px]">
          <Link
            className="inline-flex items-center gap-2 text-bodyCompact01 no-underline hover:underline"
            to={backToAlert}
            onClick={(e) => {
              e.preventDefault();
              navigate(-1);
            }}
          >
            <ArrowLeft size={16} /> {t("page.alert.pdf.back")}
          </Link>
          <h1 className="mt-3 mb-1.5 text-heading04 leading-productiveHeading05 font-productiveHeading04 md:text-heading03">
            {t("page.alert.pdf.preview")}
          </h1>
          {/* text-[13px] */}
          <p className="text-textSecondary text-bodyCompact01 leading-normal">
            {t("page.alerts.title")} {alert.alertNumber}
          </p>
        </div>
        <div className="shrink-0 w-[230px] max-md:w-full">
          <Button
            renderIcon={Download}
            disabled={!ready || printing}
            onClick={() => void print()}
            aria-describedby="pdf-preview-save-help"
            className="w-full max-w-none"
          >
            {printing ? t("page.alert.pdf.print.prep") + "..." : t("page.alert.pdf.print.save")}
          </Button>
          <p
            id="pdf-preview-save-help"
            className="mt-2 text-textSecondary text-helperText01 leading-body01 max-[680px]:mt-1"
          >
            {t("page.alert.pdf.print.save.detail")}
          </p>
        </div>
      </header>

      <section
        className="min-h-[calc(100vh-220px)] bg-layerActive overflow-y-auto h-[calc(100vh-160px)] px-5 pt-5 pb-7 md:px-7 md:pt-6 md:pb-9  print:min-h-0 print:h-auto print:overflow-visible print:bg-background"
        aria-label="Document preview"
      >
        {/* Document info bar */}
        <div className="pdf-print-hidden max-w-[210mm] mx-auto mb-4 flex justify-between flex-wrap gap-2 text-textSecondary text-label01 ">
          <span className="items-center gap-2 inline-flex">
            <DocumentPdf size={16} /> {t("page.alert.pdf.report")} {alert.alertNumber}
          </span>
          <span>A4 · {t("page.alert.pdf.portrait")}</span>
        </div>

        {printError && (
          <InlineNotification
            className="pdf-print-hidden"
            kind="error"
            hideCloseButton
            title={t("page.alert.pdf.print.unavailable")}
            subtitle={t("page.alert.pdf.print.unavailable.help")}
          />
        )}
        {logoStatus === "error" && (
          <InlineNotification
            className="pdf-print-hidden"
            kind="error"
            hideCloseButton
            title={t("page.alert.pdf.print.logo.unavailable")}
            subtitle={t("page.alert.pdf.print.logo.unavailable.help")}
          />
        )}

        {!selectedCase ? (
          <div
            className="max-w-[210mm] mx-auto min-h-[500px] bg-layer px-10 py-16 text-center flex flex-col items-center justify-center gap-6 "
            role="status"
          >
            <DocumentPdf size={40} />
            <h2 className="text-heading04">{t("page.alert.pdf.unavailable")}</h2>
            <p className="max-w-[460px] leading-expressiveParagraph01">
              {t("page.alert.pdf.print.data.unavailable")}.
            </p>
          </div>
        ) : configurationErrors.length ? (
          <div
            className="max-w-[210mm] mx-auto min-h-[500px] bg-layer px-10 py-16 text-center flex flex-col items-center justify-center gap-6 "
            role="alert"
          >
            <h2 className="text-heading04">{t("page.alert.pdf.configuration.incomplete")}</h2>
            {configurationErrors.map((error, i) => (
              <p className="max-w-[460px] leading-expressiveParagraph01 " key={i}>
                {getErrrorMessage(error)}
              </p>
            ))}
          </div>
        ) : related?.error ? (
          <p role="alert">{t("page.alert.pdf.relatedCases.load")}.</p>
        ) : (
          <article
            className={`pdf-report bg-layer w-[210mm] max-w-full p-[14mm] mx-auto text-label01 leading-[1.55] break-words min-h-[297mm] print:shadow-none${
              !ready ? " pdf-report-not-ready" : ""
            }`}
            data-report-ready={ready ? "true" : "false"}
            aria-label={`Alert report ${alert.alertNumber}`}
          >
            {/* Report header */}
            <header className="pdf-report-header flex justify-between gap-4 pb-6 border-b-2 border-solid items-center">
              <div className="flex items-center gap-2.5">
                <BrandLogo
                  className="block w-[240px] max-w-full h-auto"
                  width={240}
                  onLoad={() => setLogoStatus("ready")}
                  onError={() => setLogoStatus("error")}
                />
              </div>
              <div className="text-label01 tracking-[1px] text-right  font-headingCompact01 ">
                {title}
                <span className="block text-label01 tracking-normal font-bodyCompact01 mt-1 text-textSecondary">
                  {t("page.alert.pdf.confidential")}
                </span>
              </div>
            </header>

            {/* Intro */}
            <div className="pt-[26px] pb-[22px]">
              <p className="text-label01 tracking-[1px] font-headingCompact01">
                {caseTypeName} ·{" "}
                {selectedCase.properties
                  .find((property) => property.property.code === "id")
                  ?.value.value?.toString()}
              </p>
              <h1 className="text-heading05 leading-heading06 font-heading05 mt-2 mb-1.5">
                {t("page.alert.pdf.alert.number", { number: alert.alertNumber })}
              </h1>
            </div>

            <div className="pdf-report-reference py-4 px-5 bg-layer01 grid grid-cols-2 gap-6 mb-6 ">
              <div>
                <span className="block text-label01 mb-1 text-textSecondary">
                  {t("page.alert.pdf.reference")}
                </span>
                <strong className="text-label01">
                  {date(version === "current" ? loadedAt : selectedCase.snapshotCreation)}
                </strong>
                {versionLabel && <p className="text-label01">{versionLabel}</p>}
              </div>
              <div>
                <span className="block text-label01 mb-1 text-textSecondary">
                  {t("page.alert.pdf.prepare")}
                </span>
                <strong className="text-label01">{date(loadedAt)}</strong>
                <p className="text-label01">{Intl.DateTimeFormat().resolvedOptions().timeZone}</p>
              </div>
              {fallbackNote && (
                <p className="col-span-full -mt-2 pt-2 border-t border-solid border-t-borderSubtle text-textSecondary text-label01 leading-body02">
                  {fallbackNote}
                </p>
              )}
            </div>

            <DynamicViewRenderer
              viewConfiguration={viewConfiguration}
              print={{ caseSchema, dashboardDefinitions: [dashboard] }}
              data={{
                widgetsData: {
                  origin: alert.origin,
                  basic: {
                    item: {
                      propertyValues: selectedCase.properties,
                      elements: selectedCase.elements,
                      code: selectedCase.typeCode,
                      blacklistedIdentifiers: alert.currentCase.blacklistedIdentifiers,
                      type: "case",
                    },
                  },
                  alertWorkflow: { alert, refetch: () => undefined },
                  chronology: { alert },
                  rules: { scenarioExecutionResult: alert.scenarioExecutionResult },
                  relatedCases: {
                    caseId: selectedCase.id,
                    elements: selectedCase.elements,
                    alertId: alert.id,
                    snapshotCreation: selectedCase.snapshotCreation,
                    cases: related.data?.cases,
                    totalCount: related.data?.pageInfo.totalCount,
                  },
                },
              }}
            />

            <footer className="pdf-report-end flex justify-between flex-wrap gap-y-1 gap-x-4 mt-6 pt-2 text-textHelper text-legal01 leading-body01">
              <span>{t("page.alert.pdf.report.end")}</span>
              <span> {t("page.alert.pdf.alert.number", { number: alert.alertNumber })}</span>
            </footer>
          </article>
        )}
      </section>
    </div>
  );
}
